/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColDef, GridApi, GridReadyEvent } from '@ag-grid-community/core';
import { Component, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AgFns, GridOptionsExt, ISortModel, NotificationService, ProxAgFns, SaveAndQueryComponent, StatusService } from '@core';
import { Account, ActiveStatus, ActiveStatusEnum } from '@models';
import { BaseService } from '@core';
import { AccountDbQueryService } from './services/account-db-query.service';
import { AccountDbSaveService } from './services/account-db-save.service';
import { DialogService } from 'primeng/dynamicdialog';
import { AccountActivationService } from './services/account-activation.service';

export interface IActivationErrorParams {
  account: Account;
}

export enum AccountListEnum {
  Manage,
  Approvals,
  Users,
  Orders,
  Dashboard,
}

@Component({
  selector: 'prox-account-list',
  templateUrl: './account-list.component.html',
  standalone: false,
})
export class AccountListComponent extends SaveAndQueryComponent {
  accounts: Account[] = [];
  activeStatuses: ActiveStatus[] = [];
  isActivating = false;
  listType!: AccountListEnum;
  @ViewChild('actionCell') actionCell!: TemplateRef<any>;

  //selected: Account | null = null;
  actionSidebar: any;
  gridOptions: GridOptionsExt = {};
  gridApi: GridApi | undefined;
  frameworkComponents: any;

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    override dbQueryService: AccountDbQueryService,
    override dbSaveService: AccountDbSaveService,
    private statusService: StatusService,
    public pngDialogService: DialogService,
    public activationService: AccountActivationService,
    private notificationService: NotificationService,
  ) {
    super(baseService, route, dbSaveService, dbQueryService);
  }

  override async updateFromParams(params: object): Promise<void> {
    this.accounts = await this.dbQueryService.createQuery(Account).execute();
    this.activeStatuses = await this.dbQueryService.getAll(ActiveStatus);
    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      onFilterChanged: this.onFilterChanged,
    });
    this.activationService.deactivate();

    if (this.router.url == '/account-admin/accounts') {
      this.listType = AccountListEnum.Approvals;
      this.setTitle('Approvals - Accounts');
    } else if (this.router.url == '/account-admin/accounts-for-users') {
      this.listType = AccountListEnum.Users;
      this.setTitle('User Management - Accounts');
    } else if (this.router.url == '/account-user/accounts') {
      this.listType = AccountListEnum.Orders;
      this.setTitle('Orders - Accounts');
    } else if (this.router.url == '/account-admin/dashboard') {
      this.listType = AccountListEnum.Dashboard;
      this.setTitle('Dashboard - Accounts');
    } else {
      this.listType = AccountListEnum.Manage;
      this.setTitle('Global Administration - Accounts');
    }

    this.isPageReady = true;
  }

  onGridReady(event: GridReadyEvent) {
    const gridOptions = event.context.gridOptions;
    const [colDefs, sortModel] = this.getColDefsAndSortModel();

    AgFns.initGrid(event, colDefs, sortModel);
    // Restore before selecting, so the selected row is the first row the user actually sees.
    AgFns.restoreGridFilterModel(gridOptions, this.gridFilterStoreKey);
    AgFns.selectFirstRow(gridOptions);
  }

  /** The one component serves five routes. Each keeps its own filter so a name typed on the
   *  Orders list does not reappear on the Global Administration list. */
  private get gridFilterStoreKey() {
    return `accounts.${AccountListEnum[this.listType]}`;
  }

  onFilterChanged() {
    AgFns.saveGridFilterModel(this.gridOptions, this.gridFilterStoreKey);
  }

  getColDefsAndSortModel() {
    const colDefs: ColDef[] = [
      /*       { ...AgFns.createButtonProps('', this.onEdit.bind(this), { label: 'Edit'}), width: 50, maxWidth: 50 }, */
      { ...AgFns.createButtonProps('', this.onManage.bind(this), { label: AccountListEnum[this.listType] }), ...AgFns.fixWidth(80) },
      { headerName: 'Account Name', field: 'name', maxWidth: 400, filter: 'agTextColumnFilter', ...AgFns.flexWidth(250) },
      {
        headerName: 'Primary Administrator',
        valueGetter: (c) =>
          c.data.primaryAccountAdmin
            ? (c.data?.primaryAccountAdmin?.proximityUser?.salutation
                ? c.data?.primaryAccountAdmin?.proximityUser?.salutation?.trim() + ' '
                : '') +
              c.data?.primaryAccountAdmin?.proximityUser?.firstName?.trim() +
              ' ' +
              c.data?.primaryAccountAdmin?.proximityUser?.lastName?.trim()
            : '',
        ...AgFns.flexWidth(150),
      },
      { headerName: 'E-mail', field: 'primaryAccountAdmin.proximityUser.email', ...AgFns.flexWidth(100) },
      { headerName: 'Telephone', field: 'primaryAccountAdmin.proximityUser.telephoneNumber', ...AgFns.flexWidth(80) },
      { headerName: 'Extension', field: 'primaryAccountAdmin.proximityUser.extension', ...AgFns.flexWidth(80) },
      { headerName: 'Cell', field: 'primaryAccountAdmin.proximityUser.cellNumber', ...AgFns.flexWidth(80) },
      { ...ProxAgFns.getActiveStatusColDef('activeStatus.name'), ...AgFns.fixWidth(100) },
      ProxAgFns.getEntityStatusColDef(this.onChangeActiveStatus.bind(this), { canDisplay: this.isEditable.bind(this) }),
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];
    return [colDefs, sortModel] as const;
  }

  isEditable() {
    return this.listType === AccountListEnum.Manage;
  }

  async onChangeActiveStatus(account: Account) {
    const valFn = async (selectedStatusId: ActiveStatusEnum, errs: string[]) => {
      if (selectedStatusId != ActiveStatusEnum.Active) return true;
      if (account.activeStatusId != ActiveStatusEnum.Hold) {
        this.dialogService.okDialog('Cannot Change Status', "You must first set status to 'Hold' before activation");
        return false;
      }

      if (account.activeStatusId === ActiveStatusEnum.Hold) {
        if (await this.activationService.activate(account.id)) {
          const ok = await this.dialogService.askIfOk(
            'Cannot Activate',
            'Account cannot be activated.  Switch to Account Activation Mode for fixes?',
          );
          if (ok) {
            this.onManage(account);
            return false;
          } else {
            this.activationService.deactivate();
            return false;
          }
        }
      }
      return true;
    };
    const selectedStatusId = await this.statusService.onChangeActiveStatus(this, account, 'Account', valFn);
    this.gridOptions.apiX?.refreshCells();

    if (selectedStatusId != null) {
      if (selectedStatusId === ActiveStatusEnum.Hold) {
        const accountLink = `/account/accounts/${account.id}/manage`;
        this.notificationService.add(
          'warn',
          'Account On Hold',
          `<a href="${accountLink}">${account.name}</a> is on Hold. Remember to re-activate when editing is complete.`,
          undefined,
          `account-hold:${account.id}`,
        );
      } else {
        this.notificationService.resolveByKey(`account-hold:${account.id}`);
      }
    }
  }

  get isAccountUser() {
    return this.router.url.includes('/account-user/');
  }

  get title() {
    if (this.router.url == '/account-admin/accounts') {
      return 'Approvals and User Management - Accounts';
    } else if (this.router.url == '/account-admin/accounts-for-users') {
      return 'User Management - Accounts';
    } else if (this.router.url == '/account-user/accounts') {
      return 'Orders, Return and Cancellations - Accounts';
    } else {
      return 'Global Administration - Accounts ';
    }
  }

  onManage(row: Account) {
    if (this.router.url == '/account-admin/accounts') {
      this.router.navigate(['account-admin/accounts', row.id, 'manage', { relativeTo: this.route, queryParams: { isActivating: true } }]);
    } else if (this.router.url == '/account-admin/accounts-for-users') {
      this.router.navigate(['account-admin/accounts-for-users', row.id, 'manage']);
    } else if (this.router.url == '/account-admin/dashboard') {
      this.router.navigate(['account-admin/dashboard', row.id]);
    } else {
      this.router.navigate(['account/accounts', row.id, 'manage']);
    }
  }

  onEdit(row: Account) {
    this.router.navigate(['account/account-add-edit', row.id]);
  }

  onAdd() {
    //this.router.navigate(['add'], { relativeTo: this.route })
    this.router.navigate(['account/account-add-edit', 'add']);
  }
}
